import { useState, useEffect } from 'react';

const menuLinks = [
  { label: 'Overview', href: '#project-overview' },
  { label: 'Amenities', href: '#amenities' },
  { label: 'Location', href: '#location' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Enquiry', href: '#enquiry' },
];

const WHATSAPP_URL = 'https://wa.me/918861133745?text=Hi%2C%20I%27m%20interested%20in%20Confident%20Auriga.%20Please%20share%20more%20details.';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [mobileMenuOpen]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    setTimeout(() => {
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 350);
  };

  return (
    <>
      {/* Header Bar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-[#123524]/85 backdrop-blur-2xl border-b border-white/[0.06]'
            : 'bg-transparent'
        }`}
      >
        <div className="section-padding">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo + Branding */}
            <a
              href="#hero"
              onClick={(e) => handleNavClick(e, '#hero')}
              className="flex items-center gap-3 md:gap-[14px] group"
            >
              <img
                src="https://static.readdy.ai/image/72ebb3450643617b8a5b38c5c017e687/53e08cc6364638b80f5cc70c88cd830c.png"
                alt="Confident Auriga"
                className="h-10 md:h-12 w-auto object-contain transition-all duration-300 group-hover:opacity-80 brightness-0 invert"
              />
              <div className="flex flex-col items-start leading-none">
                <span className="font-serif text-lg md:text-[22px] tracking-wide font-semibold text-white">
                  Confident Auriga
                </span>
                <span className="font-body text-[10px] md:text-[11px] tracking-[0.2em] uppercase text-gold mt-[3px]">
                  MALUR, KARNATAKA
                </span>
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {menuLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="font-body text-sm tracking-wider uppercase text-white/90 transition-colors duration-300 hover:text-gold"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-4">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-body font-medium transition-all duration-300 ${
                  isScrolled
                    ? 'bg-gold/90 text-primary hover:bg-gold hover:shadow-lg'
                    : 'bg-white/15 backdrop-blur-sm text-white border border-white/30 hover:bg-white hover:text-primary'
                }`}
              >
                <i className="ri-whatsapp-line text-lg"></i>
                <span className="whitespace-nowrap">Enquire Now</span>
              </a>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden w-11 h-11 flex items-center justify-center rounded-full text-white hover:bg-white/10 transition-all duration-300"
                aria-label="Open menu"
                aria-expanded={mobileMenuOpen}
              >
                <i className="ri-menu-3-line text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[100] lg:hidden transition-all duration-500 ${
          mobileMenuOpen
            ? 'visible opacity-100'
            : 'invisible opacity-0 pointer-events-none'
        }`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!mobileMenuOpen}
      >
        {/* Background with cinematic zoom */}
        <div
          className={`absolute inset-0 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            mobileMenuOpen ? 'scale-100' : 'scale-[1.06]'
          }`}
        >
          <div className="absolute inset-0 bg-[#123524]/96 backdrop-blur-[40px]" />
          {/* Subtle gold glow at top */}
          <div className="absolute top-0 left-0 right-0 h-72 bg-gradient-to-b from-gold/[0.04] to-transparent pointer-events-none" />
          {/* Subtle green glow at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-56 bg-gradient-to-t from-emerald-950/20 to-transparent pointer-events-none" />
        </div>

        {/* Content */}
        <div
          className={`relative h-full flex flex-col px-6 py-6 overflow-hidden transition-opacity duration-500 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Top — Logo & Close */}
          <div className="shrink-0 flex items-center justify-between">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 350);
              }}
              className="flex items-center gap-3"
            >
              <img
                src="https://static.readdy.ai/image/72ebb3450643617b8a5b38c5c017e687/53e08cc6364638b80f5cc70c88cd830c.png"
                alt="Confident Auriga"
                className="h-10 w-auto object-contain brightness-0 invert"
              />
              <div className="flex flex-col leading-none">
                <span className="font-serif text-lg tracking-wide font-semibold text-white">
                  Confident Auriga
                </span>
                <span className="font-body text-[10px] tracking-[0.2em] uppercase text-gold mt-[3px]">
                  MALUR, KARNATAKA
                </span>
              </div>
            </button>

            <button
              onClick={() => setMobileMenuOpen(false)}
              className="w-11 h-11 flex items-center justify-center rounded-full border border-white/20 text-white hover:bg-white/10 hover:border-gold/40 hover:text-gold hover:rotate-90 transition-all duration-500"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* Decorative divider */}
          <div className="shrink-0 mt-6 mb-2 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

          {/* Middle — Navigation Links */}
          <nav className="flex-1 overflow-y-auto min-h-0 flex flex-col justify-center py-2">
            {menuLinks.map((link, i) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`group flex items-center justify-between py-5 border-b border-white/[0.06] transition-all duration-500 ${
                  mobileMenuOpen
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-5'
                }`}
                style={{ transitionDelay: `${100 + i * 60}ms` }}
              >
                <span className="font-serif text-2xl md:text-3xl text-white/90 group-hover:text-gold transition-colors duration-300 group-hover:pl-2">
                  {link.label}
                </span>
                <i className="ri-arrow-right-line text-white/20 group-hover:text-gold group-hover:translate-x-2 transition-all duration-500 text-xl" />
              </a>
            ))}
          </nav>

          {/* Bottom — WhatsApp CTA */}
          <div
            className={`shrink-0 pt-4 pb-2 transition-all duration-500 ${
              mobileMenuOpen
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-5'
            }`}
            style={{
              transitionDelay: `${100 + menuLinks.length * 60}ms`,
            }}
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-gradient-to-r from-emerald-700/90 via-green-600/90 to-emerald-700/90 text-white font-body font-semibold text-sm tracking-wider backdrop-blur-sm border border-white/10 shadow-[0_4px_20px_rgba(34,197,94,0.25)] hover:shadow-[0_8px_40px_rgba(34,197,94,0.45)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-500"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              WhatsApp Enquiry
            </a>
          </div>
        </div>
      </div>
    </>
  );
}